import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { BrandLogo } from '../../components/brand/BrandLogo';
import { PrivacyChoicesButton } from '../components/consent/PrivacyChoicesButton';
import { signInWithPasskey, supportsPasskeys } from '../lib/passkeys';
import {
  redirectToSocialAuth,
  type SocialAuthProviderId,
} from '../lib/safe-navigation';

type LoginProviderId = SocialAuthProviderId;

interface LoginPageProps {
  authBaseUrl?: string;
  passkey?: boolean;
  providers?: { github?: boolean; google?: boolean; microsoft?: boolean };
}

export function LoginPage({ authBaseUrl, passkey, providers }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<'request' | 'verify'>('request');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedRedirect = params.get('redirectTo');
  const redirectTo =
    requestedRedirect?.startsWith('/') && !requestedRedirect.startsWith('//')
      ? requestedRedirect
      : location.pathname.startsWith('/app')
      ? `${location.pathname}${location.search}${location.hash}`
      : '/app';
  const passkeyAvailable = Boolean(passkey) && supportsPasskeys();

  const completeSignIn = async () => {
    await queryClient.invalidateQueries();
    if (redirectTo.startsWith('/api/')) {
      window.location.href = redirectTo;
    } else {
      navigate(redirectTo, { replace: true });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!authBaseUrl) return;
    setLoading(true);
    setError(null);

    try {
      if (stage === 'request') {
        const response = await fetch(`${authBaseUrl}/email-otp/send-verification-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, type: 'sign-in' }),
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({} as any));
          throw new Error(payload?.error?.message || `Unable to send code (${response.status})`);
        }
        setStage('verify');
      } else {
        const response = await fetch(`${authBaseUrl}/sign-in/email-otp`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, otp }),
        });
        const payload = await response.json().catch(() => ({} as any));
        if (!response.ok) {
          throw new Error(payload?.error?.message || `Invalid code (${response.status})`);
        }
        await completeSignIn();
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeySignIn = async () => {
    if (!authBaseUrl) return;
    setError(null);
    setLoading(true);
    setPasskeyLoading(true);

    try {
      await signInWithPasskey(authBaseUrl);
      await completeSignIn();
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Passkey sign-in failed');
    } finally {
      setPasskeyLoading(false);
      setLoading(false);
    }
  };

  const handleProviderSignIn = async (provider: LoginProviderId) => {
    if (!authBaseUrl) return;
    const callbackURL = `${window.location.origin}${redirectTo}`;
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(
        `${authBaseUrl}/sign-in/social`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ provider, callbackURL }),
        }
      );
      const payload = await response.json().catch(
        () => ({} as { url?: string; error?: { message?: string } })
      );
      if (!response.ok || !payload.url) {
        throw new Error(
          payload.error?.message || `Unable to start ${provider} sign-in (${response.status})`
        );
      }
      redirectToSocialAuth(provider, payload.url);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Authentication failed');
      setLoading(false);
    }
  };

  const socialProviders = (
    [
      { id: 'github', label: 'GitHub' },
      { id: 'google', label: 'Google' },
      { id: 'microsoft', label: 'Microsoft' },
    ] as const
  ).filter((provider) => Boolean(providers?.[provider.id]));

  return (
    <main className="min-h-screen bg-slate-950 text-slate-100">
      <div className="mx-auto flex min-h-screen max-w-6xl items-center justify-center px-6 py-16">
        <div className="w-full max-w-[520px]">
          <section className="rounded-2xl border border-slate-800 bg-slate-900/90 p-8 shadow-2xl shadow-slate-950/50">
            <div className="mb-8">
              <div className="theme-brand-lockup inline-flex items-center gap-3 px-2.5 py-2">
                <BrandLogo variant="mark" className="h-8 w-8 shrink-0" />
                <span className="text-sm font-semibold uppercase tracking-[0.2em] text-cyan-300">
                  Perspectify
                </span>
              </div>
              <h1 className="mt-3 text-2xl font-semibold text-white">
                Sign in to your workspace
              </h1>
            </div>
            <form onSubmit={(event) => void handleSubmit(event)} className="mt-6 space-y-4">
              <label className="block text-sm font-medium text-slate-200">
                Email
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  placeholder="you@company.com"
                  className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/40 focus:outline-none focus:ring-2 focus:ring-cyan-400/15"
                />
              </label>
              {stage === 'verify' && (
                <label className="block text-sm font-medium text-slate-200">
                  Code
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={otp}
                    onChange={(event) => setOtp(event.target.value)}
                    required
                    placeholder="6-digit code"
                    className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm tracking-widest text-slate-100 placeholder:text-slate-500 focus:border-cyan-400/40 focus:outline-none focus:ring-2 focus:ring-cyan-400/15"
                  />
                </label>
              )}

              {error && (
                <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-cyan-400 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:bg-cyan-200"
              >
                {loading ? (stage === 'request' ? 'Sending code…' : 'Verifying…') : stage === 'request' ? 'Send code' : 'Verify & sign in'}
              </button>
            </form>

            {(passkeyAvailable || socialProviders.length > 0) && (
              <>
                <div className="my-6 flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-800" />
                  <span className="text-xs text-slate-500">Or continue with</span>
                  <div className="h-px flex-1 bg-slate-800" />
                </div>

                <div className="grid gap-3">
                  {passkeyAvailable && (
                    <button
                      type="button"
                      onClick={() => void handlePasskeySignIn()}
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm text-slate-300 transition hover:border-cyan-300/30 hover:text-slate-100 disabled:opacity-60"
                    >
                      <KeyRound className="h-5 w-5" aria-hidden="true" />
                      {passkeyLoading ? 'Waiting for passkey…' : 'Sign in with a passkey'}
                    </button>
                  )}
                  {socialProviders.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      onClick={() => void handleProviderSignIn(provider.id)}
                      disabled={loading}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-950/60 px-4 py-3 text-sm text-slate-300 transition hover:border-cyan-300/30 hover:text-slate-100 disabled:opacity-60"
                    >
                      {provider.id === 'github' && (
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                          <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
                        </svg>
                      )}
                      {provider.id === 'google' && (
                        <svg viewBox="0 0 24 24" className="h-5 w-5">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                      )}
                      {provider.id === 'microsoft' && (
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                          <path fill="#F25022" d="M2 2h9.5v9.5H2z" />
                          <path fill="#7FBA00" d="M12.5 2H22v9.5h-9.5z" />
                          <path fill="#00A4EF" d="M2 12.5h9.5V22H2z" />
                          <path fill="#FFB900" d="M12.5 12.5H22V22h-9.5z" />
                        </svg>
                      )}
                      Sign in with {provider.label}
                    </button>
                  ))}
                </div>
              </>
            )}

            <p className="mt-6 text-center text-sm text-slate-500">
              Need access?{' '}
              <a href="https://perspectify.dev/contact-sales?source=app-login" className="text-cyan-400 hover:text-cyan-300">
                Contact sales
              </a>
            </p>
          </section>
          <div className="mt-4 flex justify-center">
            <PrivacyChoicesButton variant="link" />
          </div>
        </div>
      </div>
    </main>
  );
}
